import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileSignature, Printer, Check } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import DatePickerField from '@/components/DatePickerField';

const it = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '____________';
const riga = (etichetta, valore) => `<p style="margin:3px 0"><b>${etichetta}:</b> ${valore || '________________________'}</p>`;

export default function ModuloConsenso({ contenzione, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    consenso_firmato_da: contenzione.consenso_firmato_da || '',
    parentela: contenzione.consenso_parentela || '',
    data_consenso: contenzione.data_consenso || new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);

  const stampa = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/contenzioni/${contenzione.id}/modulo-consenso`);
      const c = d.contenzione;
      openPrintWindow('Consenso informato alla contenzione', `
        <h2 style="text-align:center">Consenso informato all'utilizzo di mezzi di contenzione</h2>
        <p style="font-size:11px;text-align:center">${d.struttura.nome || ''} ${d.struttura.indirizzo ? `- ${d.struttura.indirizzo}, ${d.struttura.citta}` : ''}</p>

        <h3 style="margin:14px 0 4px;font-size:13px">1. Dati dell'ospite</h3>
        ${riga('Nome e cognome', `${d.ospite.cognome} ${d.ospite.nome}`)}
        ${riga('Nato/a il', `${it(d.ospite.data_nascita)} ${d.ospite.luogo_nascita ? `a ${d.ospite.luogo_nascita}` : ''}`)}
        ${riga('Collocazione', `camera ${d.ospite.stanza || '-'} letto ${d.ospite.letto || '-'} - area ${d.ospite.area || '-'}`)}
        ${riga('Medico curante', d.ospite.medico_base)}

        <h3 style="margin:14px 0 4px;font-size:13px">2. Familiare o amministratore di sostegno</h3>
        ${riga('Sig./Sig.ra', d.familiare.nome)}
        ${riga('In qualita di', d.familiare.parentela)}
        ${riga('Recapito telefonico', d.familiare.telefono)}

        <h3 style="margin:14px 0 4px;font-size:13px">3. Misura di contenzione proposta</h3>
        ${riga('Tipo di dispositivo', c.tipo)}
        ${riga('Fascia di applicazione', c.fascia)}
        ${riga('Data di inizio prevista', it(c.data_inizio))}
        ${riga('Motivazione clinica', c.motivazione)}
        ${riga('Alternative gia tentate', c.alternative_tentate || 'Nessuna alternativa documentata')}
        ${riga('Prescrizione medica', c.prescrizione_medica
          ? `${c.medico_prescrittore || 'presente'} del ${it(c.data_prescrizione)}`
          : 'DA ACQUISIRE prima dell\'applicazione')}

        <h3 style="margin:14px 0 4px;font-size:13px">4. Informazioni fornite</h3>
        <p style="font-size:11px;margin:3px 0"><b>Benefici attesi:</b></p>
        <ul style="font-size:11px;margin:0;padding-left:18px">${d.benefici.map(x => `<li>${x}</li>`).join('')}</ul>
        <p style="font-size:11px;margin:6px 0 3px"><b>Rischi e possibili effetti indesiderati:</b></p>
        <ul style="font-size:11px;margin:0;padding-left:18px">${d.rischi.map(x => `<li>${x}</li>`).join('')}</ul>

        <h3 style="margin:14px 0 4px;font-size:13px">5. Durata, rivalutazione e revoca</h3>
        <p style="font-size:11px">La contenzione e una misura <b>eccezionale e temporanea</b>, applicata per il tempo
        strettamente necessario. Sara <b>rivalutata almeno ogni ${d.rivalutazione_giorni} giorni</b> dall'equipe
        assistenziale, con registrazione dell'esito in cartella, e sospesa appena vengono meno le condizioni che
        l'hanno resa necessaria. Il consenso puo essere <b>revocato in qualsiasi momento</b>, in forma scritta o
        verbale, senza alcuna conseguenza sulla qualita dell'assistenza prestata.</p>

        <h3 style="margin:14px 0 4px;font-size:13px">6. Riferimenti normativi</h3>
        <ul style="font-size:10px;margin:0;padding-left:18px">${d.riferimenti_normativi.map(x => `<li>${x}</li>`).join('')}</ul>

        <h3 style="margin:14px 0 4px;font-size:13px">7. Dichiarazione di consenso</h3>
        <p style="font-size:11px">Il/La sottoscritto/a, informato/a in modo chiaro e completo sulle motivazioni,
        sulle modalita, sui benefici e sui rischi della misura di contenzione sopra descritta, e consapevole
        della possibilita di revocare il consenso in ogni momento,</p>
        <p style="font-size:12px;margin:8px 0"><b>&#9744; ACCONSENTE &nbsp;&nbsp;&nbsp; &#9744; NON ACCONSENTE</b>
        all'applicazione della misura di contenzione indicata.</p>

        <p style="font-size:11px;margin-top:16px">Luogo e data ____________________________________</p>
        <table style="width:100%;margin-top:26px;font-size:11px">
          <tr>
            <td width="33%">Il familiare / amm. di sostegno<br/><br/>____________________________</td>
            <td width="33%">Il medico prescrittore<br/><br/>____________________________</td>
            <td width="33%">Il Coordinatore<br/><br/>____________________________</td>
          </tr>
        </table>
        <p style="font-size:10px;margin-top:14px;color:#555">Modulo predisposto il ${it(d.generato_il)} da ${d.generato_da}.
        Una copia va consegnata al familiare e una conservata nella cartella dell'ospite.</p>
      `);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nella generazione del modulo'); }
    setBusy(false);
  };

  const registra = async () => {
    if (!form.consenso_firmato_da.trim()) return toast.error('Indica chi ha firmato il consenso');
    setBusy(true);
    try {
      await api.put(`/contenzioni/${contenzione.id}/consenso`, { ...form, consenso_familiare: true });
      toast.success('Consenso registrato in cartella');
      setOpen(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setBusy(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={stampa} disabled={busy}
        data-testid={`stampa-consenso-${contenzione.id}`}>
        <Printer className="w-3 h-3" />Modulo consenso
      </Button>
      {!contenzione.consenso_familiare && (
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setOpen(true)}
          data-testid={`registra-consenso-${contenzione.id}`}>
          <FileSignature className="w-3 h-3" />Registra firma
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registra il consenso firmato</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Firmato da *</Label>
              <Input value={form.consenso_firmato_da} onChange={e => setForm(f => ({ ...f, consenso_firmato_da: e.target.value }))}
                placeholder="Nome e cognome del familiare" data-testid="consenso-firmato-da" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">In qualita di</Label>
                <Input value={form.parentela} onChange={e => setForm(f => ({ ...f, parentela: e.target.value }))}
                  placeholder="Figlio, coniuge, tutore..." /></div>
              <div><Label className="text-xs">Data della firma *</Label>
                <DatePickerField value={form.data_consenso} onChange={v => setForm(f => ({ ...f, data_consenso: v }))} testId="consenso-data" /></div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={registra} disabled={busy} className="bg-brand hover:bg-brand-dark text-white gap-1" data-testid="salva-consenso">
                <Check className="w-3.5 h-3.5" />{busy ? 'Salvataggio...' : 'Registra consenso'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
